import { DateTime } from 'luxon';
import { isUndefined } from 'lodash';

import { Beneficiary } from 'theSource/components/Beneficiaries';
import { BenefitDto, FamilyMemberDto, BeneficiaryDto } from 'theSource/queries';
import { API_DATE_FORMAT, convertToDateString } from 'theSource/utils';
import { BeneficiariesDto } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery/types';

export interface DetailedBenefit {
  coverageId?: number;
  name?: string;
  logoUrl?: string;
  paymentSize?: number;
  coverageStart?: string;
  coverageFrom?: string;
  coverageTo?: string;
  isWaived?: boolean;
  terminationDate?: string;
  orgPlanId?: number;
  orgInfoBenId?: number;
  coverageLevel?: string;
  employerContribution?: string;
  employerFsaAmount?: string;
  employeeFsaAmount?: string;
  volume?: string;
  volumeText?: string;
  benefitClass?: string;
  premiumMethod?: string;
  familyMembers?: FamilyMember[];
  beneficiaries?: Beneficiary[];
  carrierLookupCode?: string;
  hideEmptyCost?: boolean;
}

export interface FamilyMember {
  id: number;
  name: string;
  relationship: string;
}

export const mapBeneficiaries = (
  beneficiaries?: BeneficiariesDto[]
): Beneficiary[] => {
  if (!beneficiaries) {
    return [];
  }
  return beneficiaries.map((beneficiary) => ({
    id: beneficiary.id || 0,
    name: beneficiary.name,
    relationship: beneficiary.relationship,
    isPrimary: beneficiary.primary_beneficiary,
    allocation: beneficiary.allocation,
  }));
};

export const mapDetailedBenefit = (
  benefits: BenefitDto[],
  orgInformationalBenefits: BenefitDto[],
  lookupCode: string
): DetailedBenefit => {
  let selectedBenefit = benefits.find(
    (benefit) => benefit.benefit_lookup_code === lookupCode
  );

  if (isUndefined(selectedBenefit)) {
    selectedBenefit = orgInformationalBenefits.find(
      (benefit) => benefit.benefit_lookup_code === lookupCode
    );
  }
  return {
    coverageId: selectedBenefit?.coverage?.coverage_id,
    name:
      selectedBenefit?.coverage?.org_plan_name ||
      selectedBenefit?.org_benefit_name,
    logoUrl:
      selectedBenefit?.coverage?.carrier_logo_url ||
      selectedBenefit?.carrier_logo_url,
    paymentSize: selectedBenefit?.coverage?.subscriber_premium,
    coverageStart: selectedBenefit?.coverage?.original_effective_date,
    coverageFrom: selectedBenefit?.coverage?.change_effective_date,
    coverageTo:
      selectedBenefit?.coverage?.termination_date ??
      selectedBenefit?.coverage?.plan_year_end_date,
    isWaived: selectedBenefit?.coverage?.waived,
    orgPlanId: selectedBenefit?.coverage?.org_plan_id,
    orgInfoBenId: selectedBenefit?.id,
    coverageLevel: selectedBenefit?.coverage?.coverage_level_name,
    employerContribution: selectedBenefit?.coverage?.org_premium,
    employerFsaAmount: selectedBenefit?.coverage?.org_fsa_amount,
    employeeFsaAmount: selectedBenefit?.coverage?.subscriber_fsa_amount,
    volume: selectedBenefit?.coverage?.volume,
    volumeText: selectedBenefit?.coverage?.volume_text,
    benefitClass: selectedBenefit?.benefit_class,
    premiumMethod: selectedBenefit?.premium_method,
    familyMembers: selectedBenefit?.coverage?.req_dependents?.map(
      (dependent: FamilyMemberDto) => ({
        id: dependent.dependent_id,
        name: dependent.dependent_name,
        relationship: dependent.relationship_code,
      })
    ),
    beneficiaries: selectedBenefit?.coverage?.beneficiaries?.map(
      (person: BeneficiaryDto) => ({
        id: person.id,
        name: person.name,
        relationship: person.relationship,
        isPrimary: person.primary_beneficiary,
        allocation: person.allocation,
      })
    ),
    carrierLookupCode: selectedBenefit?.coverage?.carrier_lookup_code,
    hideEmptyCost: selectedBenefit?.hide_cost_if_empty,
  };
};

export const getCoveragePeriod = (
  dateFrom: string | undefined,
  dateTo: string | undefined,
  divider: string
): string => {
  const convertToDate = (date: string | undefined): string => {
    if (!date) {
      return '';
    }
    return convertToDateString(DateTime.fromFormat(date, API_DATE_FORMAT));
  };

  return `${convertToDate(dateFrom)} ${divider} ${convertToDate(dateTo)}`;
};
